import { AxiosInstance } from "axios";
import { failure, ok, pending, Result } from "../../common/common";
import { makeAutoObservable } from "mobx";
import { OptionFieldModel, RadioFieldModel, StringFieldModel } from "../../common/commonForms";
import AlertMessageStore from "../../stores/AlertMessageStore";
import { SelectedPortfolioOption, sumFollowUpCounts } from "../portfolioDomain";
import UserStore from "../../stores/UserStore";
import { AccessType } from "../../types/enums/AccessType";

export class FollowUpShareholdersStore {
  fetchResponse?: Result<GetShareholdersFollowUpResponse>;
  showReviewDialogData?: {
    data: ShareholderInfoFollowUp;
    model: OptionFieldModel<ShareholderFollowUpItem>;
  };
  isSubmitting = false;
  nameFilter = new StringFieldModel();
  companyFilter: RadioFieldModel<{ orgNo: string; name: string } | null>;

  constructor(
    readonly http: AxiosInstance,
    readonly messageStore: AlertMessageStore,
    readonly userStore: UserStore,
    readonly portfolio: SelectedPortfolioOption,
    readonly contextCompany?: string,
  ) {
    this.companyFilter = new RadioFieldModel<{ orgNo: string; name: string } | null>({
      value: null,
      options: [],
      idFn: (value) => (value ? value.orgNo : "all"),
      labelFn: (value) => (value ? { translated: value.name } : { key: "portfolio.all-companies" }),
    });

    makeAutoObservable(
      this,
      { http: false, messageStore: false, userStore: false },
      { autoBind: true },
    );
  }

  *fetchFollowUps() {
    this.fetchResponse = pending();
    try {
      const url = toFollowUpShareholdersApiUrl(this.portfolio, this.contextCompany);
      if (!url) return;

      const response: GetShareholdersFollowUpResponse = yield this.http
        .get<GetShareholdersFollowUpResponse>(url)
        .then((res) => res.data);
      this.fetchResponse = ok(response);
      this.userStore.setPortfolioFollowUpCount(sumFollowUpCounts(response));
      const options = response.followUp
        .filter((f) => !f.isDismissed)
        .map((d) => ({
          orgNo: d.orgNo,
          name: d.companyName,
        }));
      this.companyFilter.updateOptions([null, ...options]);
    } catch (e) {
      this.fetchResponse = failure();
      this.messageStore.showGeneralError();
    }
  }

  openReviewDialog(data: ShareholderInfoFollowUp) {
    this.showReviewDialogData = {
      data,
      model: new OptionFieldModel({
        selectedIds: data.shareholders.map((s) => s.companyNumber),
        options: data.shareholders,
        idFn: (value) => value.companyNumber,
        labelFn: (value) => ({ translated: value.companyName }),
      }),
    };
  }

  closeReviewDialog() {
    this.showReviewDialogData = undefined;
  }

  *submitReviewDialog() {
    this.isSubmitting = true;
    try {
      const shareholderOrgNos = this.showReviewDialogData?.model.selectedIds;
      yield this.http.post("/portfolio/follow-up/shareholders/update", {
        orgNo: this.showReviewDialogData?.data.orgNo,
        shareholderOrgNos: shareholderOrgNos,
      });
      this.showReviewDialogData = undefined;
      this.messageStore.showSuccess({
        key: "followup.shareholders.dialog-success",
        params: {
          count: shareholderOrgNos?.size,
        },
      });

      this.fetchFollowUps();
    } catch (e) {
      this.messageStore.showGeneralError();
    } finally {
      this.isSubmitting = false;
    }
  }

  *dismissUpdates(orgNo: string) {
    try {
      yield this.http.delete(`/portfolio/follow-up/shareholders/${orgNo}`);
      this.fetchFollowUps();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  filterData(data: ShareholderFollowUp[]) {
    const companyFilter = this.companyFilter.value;
    const filteredData = companyFilter ? data.filter((d) => d.orgNo === companyFilter.orgNo) : data;
    const nameFilter = this.nameFilter.inputValue.toLowerCase().trim();
    return nameFilter
      ? filteredData.filter((x) => x.companyName.toLowerCase().includes(nameFilter))
      : filteredData;
  }
}

export type GetShareholdersFollowUpResponse = {
  followUp: ShareholderFollowUp[];
  signDocumentCount: number;
  followUpMattersCount: number;
  companyInfoCount: number;
  followUpShareholdersCount: number;
};

export type ShareholderFollowUp = ShareholderInfoFollowUp;

export type ShareholderInfoFollowUp = {
  type: "shareholder-info";
  orgNo: string;
  companyName: string;
  shareholders: ShareholderFollowUpItem[];
  isDismissed: boolean;
};

export type ShareholderFollowUpItem = {
  companyName: string;
  companyNumber: string;
  brName: string;
};

const toFollowUpShareholdersApiUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  contextCompany?: string,
): string | null => {
  const format = (part: string, context: string = "") =>
    "/portfolio" + part.toLowerCase() + "/follow-up/shareholders" + context;
  switch (selectedPortfolioOption.type) {
    case "all-companies":
      return format("", contextCompany ? `/${contextCompany}` : "");
    case "with-admin-role":
      return format(`/role/${AccessType.ADMIN}`);
    case "with-board-role":
      return null;
    case "my-investments":
      return null;
    case "portfolio-instance":
      return format(`/${selectedPortfolioOption.portfolio.id}`);
  }
};

export const followUpShareholderTabs = ["followup", "dismissed"] as const;

export type ShareholderFollowUpTab = (typeof followUpShareholderTabs)[number];

export const followUpShareholderTabToLabel = {
  followup: "follow-up.shareholders.tab-follow-up",
  dismissed: "follow-up.shareholders.tab-dismissed",
} as const;

export function mapToFollowUpShareholderTab(tab?: string): ShareholderFollowUpTab {
  return tab === "dismissed" ? "dismissed" : "followup";
}

export function filterShareholderFollowUps(
  followUps: ShareholderFollowUp[],
  tab: ShareholderFollowUpTab,
) {
  return tab === "followup"
    ? followUps.filter((f) => !f.isDismissed)
    : followUps.filter((f) => f.isDismissed);
}
